/**
 * Microtonal Forge — Core type definitions
 *
 * A "Keyboard" bundles a Scale plus a mapping of physical keys to pitches.
 * Scales can use any repeating interval (period), not just the octave (2/1).
 * Bohlen-Pierce, Wendy Carlos tunings, equal divisions, just-intonation, etc.
 * are all expressible.
 */

/** A single step within one period of a scale, measured in cents from the root. */
export interface ScaleStep {
  /** Cents above the root of the period (e.g. 0, 100, 200, … for 12-EDO). */
  cents: number;
  /** Optional human-readable label, e.g. "5/4" or "A". */
  label?: string;
  /** Optional ratio string that was used to derive cents, e.g. "3/2". */
  ratio?: string;
}

/** A repeating tuning system. The period may be the octave (1200 c) or any other interval. */
export interface Scale {
  id: string;
  name: string;
  description?: string;
  /** Size of the repeating interval in cents. 1200 = octave, 1901.955 = tritave (3/1). */
  periodCents: number;
  /**
   * Scale steps within one period, **excluding** the implicit root (0 c) and
   * **excluding** the period itself. For 12-EDO this has 11 entries
   * (100, 200, … 1100). Degree 0 (the root) is always implicit.
   */
  steps: ScaleStep[];
  /** Frequency in Hz of degree 0 of period 0. */
  baseFrequency: number;
  /** Is this a built-in (read-only) scale? */
  builtIn?: boolean;
}

/**
 * A key on the keyboard. Each key plays a specific scale degree in a specific
 * period repetition. E.g. degree=3, period=-1 plays one octave below root degree 3.
 */
export interface KeyboardKey {
  /** Unique id for React keys & internal tracking. */
  id: string;
  /** Scale degree index (0 = root of the period). */
  degree: number;
  /** Period repetition offset (0 = base octave, 1 = up one period, -1 = down). */
  period: number;
  /** Optional keyboard shortcut label, e.g. "a". */
  keyLabel?: string;
  /** Key type: "white" (full-height bottom row), "black" (shorter, overlaid on top), "accent" (root, rendered like white but highlighted). */
  color?: 'white' | 'black' | 'accent';
  /** Optional note name displayed on the key, e.g. "C", "C#", "D4". */
  noteName?: string;
}

/** A complete playable keyboard: a scale + a layout of keys. */
export interface Keyboard {
  id: string;
  name: string;
  description?: string;
  /** The scale this keyboard is built from. */
  scale: Scale;
  /** Ordered list of keys (left to right). */
  keys: KeyboardKey[];
  /** Is this a built-in (read-only) keyboard? */
  builtIn?: boolean;
}

// ---------------------------------------------------------------------------
// Nostr sharing types (kind 37357 addressable events)
// ---------------------------------------------------------------------------

export const MICROTONAL_KIND = 37357;

/** JSON payload stored in the `content` of a kind 37357 event. */
export interface MicrotonalNostrPayload {
  kind: 'keyboard';
  keyboard: Omit<Keyboard, 'id'>;
  /** Version of the payload schema. */
  v: 1;
}
